// src/axiosInstance.js
import axios from 'axios';

// Create Axios instance with cookie credentials
const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    withCredentials: true, // ✅ send cookies like access/refresh and csrftoken
});

// 🔄 Token refresh logic
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// ✅ Automatically attach CSRF token from cookie
api.interceptors.request.use(
    (config) => {
        const csrfToken = getCookie("csrftoken");
        if (csrfToken) {
            config.headers["X-CSRFToken"] = csrfToken;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// 🚨 Refresh token handler
api.interceptors.response.use(
    res => res,
    async err => {
        const originalRequest = err.config;

        // Skip refresh logic for login or auth endpoints
        if (
            originalRequest.url.includes("/auth/social/login/") ||
            originalRequest.url.includes("/auth/login/") ||
            originalRequest.url.includes("/auth/register/")
        ) {
            return Promise.reject(err);
        }

        // If not 401 or already retried, don't refresh
        if (err.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(err);
        }

        originalRequest._retry = true;

        if (isRefreshing) {
            // Wait if refresh already happening
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then(() => api(originalRequest))
                .catch(Promise.reject);
        }

        isRefreshing = true;

        try {
            await api.post(
                `/auth/refresh-cookie/`,
            );

            isRefreshing = false;
            processQueue(null);
            return api(originalRequest); // Retry original request
        } catch (refreshError) {
            isRefreshing = false;
            processQueue(refreshError, null);
            return Promise.reject(refreshError);
        }
    }
);

// 🍪 Helper to extract cookie
function getCookie(name) {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? decodeURIComponent(match[2]) : null;
}


// ✅ 🔐 One-time CSRF cookie fetch
api.get("/auth/csrf/")
    .then(() => {})
    .catch((err) => {});
export default api;
